/**
 * Reward Caps — monitor + alter the abuse ceilings on action-based point
 * awards (POST /rewards/award). The ceilings live in DB config, so edits here
 * take effect on the next award with no deploy. The 24h snapshot and the
 * "users at the daily cap today" count are the farming signal.
 */
import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { useRewardCaps, useUpdateRewardCaps } from '@/hooks/useRewardCaps';
import { Card, SectionLabel, StatCard, Skeleton } from '@/components/ui/atoms';
import { fmtNum } from '@/lib/format';

export function RewardCapsScreen() {
  const { data, isLoading, error } = useRewardCaps();
  const update = useUpdateRewardCaps();

  const [maxSingle, setMaxSingle] = useState('');
  const [daily, setDaily] = useState('');

  // Seed the inputs from the server values once loaded, and whenever the
  // server values actually change (e.g. after a save or an edit elsewhere).
  const srvMax = data?.limits?.maxSingleAward;
  const srvDaily = data?.limits?.dailyActionPointCap;
  useEffect(() => {
    if (srvMax !== undefined) setMaxSingle(String(srvMax));
    if (srvDaily !== undefined) setDaily(String(srvDaily));
  }, [srvMax, srvDaily]);

  const m = data?.monitor;
  const atCap = m?.usersAtDailyCapToday ?? 0;

  const dirty =
    data?.limits !== undefined &&
    (String(srvMax) !== maxSingle || String(srvDaily) !== daily);

  const onSave = () => {
    update.mutate({
      maxSingleAward: Number(maxSingle),
      dailyActionPointCap: Number(daily)
    });
  };

  const saveErr =
    (update.error as { response?: { data?: { errors?: { msg?: string } } } })
      ?.response?.data?.errors?.msg;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-nurev-text">Reward Caps</h1>
        <p className="mt-1 text-sm text-nurev-muted">
          Abuse ceilings for action-based point awards. Change them live — no
          deploy, effective on the next award.
        </p>
      </div>

      {error && (
        <Card className="text-sm text-rose-300">
          Couldn’t load reward caps. Check that nurev-api is reachable.
        </Card>
      )}

      {/* ─── monitor ─────────────────────────────────────────────── */}
      <div>
        <SectionLabel>Last 24 hours</SectionLabel>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            label="Action points awarded"
            value={fmtNum(m?.totalActionPointsAwarded ?? 0)}
            loading={isLoading}
          />
          <StatCard
            label="Awards"
            value={fmtNum(m?.awardCount ?? 0)}
            loading={isLoading}
          />
          <StatCard
            label="Distinct users"
            value={fmtNum(m?.distinctUsers ?? 0)}
            loading={isLoading}
          />
          <StatCard
            label="Users at daily cap"
            value={fmtNum(atCap)}
            color={atCap > 0 ? 'text-amber-300' : undefined}
            sub={atCap > 0 ? 'possible farming — review below' : 'none maxed today'}
            loading={isLoading}
          />
        </div>
      </div>

      {/* ─── edit caps ───────────────────────────────────────────── */}
      <div>
        <SectionLabel>Caps</SectionLabel>
        <Card>
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium text-nurev-muted">
                Max per single action (points)
              </span>
              <input
                type="number"
                min={1}
                value={maxSingle}
                onChange={e => setMaxSingle(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-nurev-border bg-nurev-surface-lift px-3 py-2 text-sm text-nurev-text outline-none focus:border-nurev-border-strong"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-nurev-muted">
                Daily cap per user (points)
              </span>
              <input
                type="number"
                min={1}
                value={daily}
                onChange={e => setDaily(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-nurev-border bg-nurev-surface-lift px-3 py-2 text-sm text-nurev-text outline-none focus:border-nurev-border-strong"
              />
            </label>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={onSave}
              disabled={!dirty || update.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-[#6c4fd4] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5a3cc4] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Save size={15} /> {update.isPending ? 'Saving…' : 'Save caps'}
            </button>
            {update.isError && (
              <span className="text-xs text-rose-300">
                {saveErr || 'Save failed.'}
              </span>
            )}
            {update.isSuccess && !dirty && (
              <span className="text-xs text-emerald-300">Saved.</span>
            )}
          </div>
          <p className="mt-3 text-[11px] text-nurev-muted">
            A single award can never exceed the daily cap.
          </p>
        </Card>
      </div>

      {/* ─── top awardees ────────────────────────────────────────── */}
      <div>
        <SectionLabel>Top awardees today</SectionLabel>
        <Card className="overflow-hidden p-0">
          {isLoading ? (
            <div className="p-5">
              <Skeleton className="h-24 w-full" />
            </div>
          ) : !m?.topAwardeesToday?.length ? (
            <div className="p-5 text-sm text-nurev-muted">
              No action-based points awarded today.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                  <th className="px-5 py-3 font-medium">Publisher user</th>
                  <th className="px-5 py-3 font-medium">Publisher</th>
                  <th className="px-5 py-3 text-right font-medium">Points today</th>
                </tr>
              </thead>
              <tbody>
                {m.topAwardeesToday.map((u, i) => {
                  const maxed =
                    srvDaily !== undefined && u.pointsToday >= srvDaily;
                  return (
                    <tr
                      key={`${u.publisherId}-${u.publisherUserId}-${i}`}
                      className="border-b border-nurev-border/60 last:border-0"
                    >
                      <td className="px-5 py-3 font-mono text-xs text-nurev-text">
                        {u.publisherUserId}
                      </td>
                      <td className="px-5 py-3 font-mono text-[11px] text-nurev-muted">
                        {u.publisherId}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">
                        <span className={maxed ? 'text-amber-300' : 'text-nurev-text'}>
                          {fmtNum(u.pointsToday)}
                        </span>
                        {maxed && (
                          <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-300 ring-1 ring-amber-500/30">
                            at cap
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
